import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

interface FAQsPageProps {
  onNavigate?: (view: string) => void;
}

interface FAQ {
  question: string;
  answer: string;
}

const allFaqs: FAQ[] = [
  {
    question: 'What pages are available in the dashboard?',
    answer: 'The dashboard has the following pages: Home (overview), Rules (view and manage rules), Approvals (admin only — approve or reject rules), Audit Log (admin only — track all rule changes), Docs (documentation), and FAQs.',
  },
  {
    question: 'How do I create a new rule?',
    answer: 'Go to the Rules page and click the blue circle button at the bottom-right corner. Fill in the required fields (Rule Name, Redirect URL, Priority), set the schedule, add matching criteria, and click Submit.',
  },
  {
    question: 'What fields are required when creating a rule?',
    answer: 'Rule Name, Redirect URL, and Priority are required. You also need at least one matching criterion such as Style Codes, Style Color Codes, Country, GTINs, QR Codes, or Bounding Box.',
  },
  {
    question: 'What happens after I submit a rule?',
    answer: 'The rule goes to the Approvals page with a "Submitted" status. An admin will review and either approve or reject it.',
  },
  {
    question: 'What format should the Redirect URL be in?',
    answer: 'The Redirect URL must start with https://.',
  },
  {
    question: 'Can two rules have the same name?',
    answer: 'No, every rule name must be unique. The system will show an error if the name already exists.',
  },
  {
    question: 'How do I view a rule\'s details?',
    answer: 'Click on any rule card in the Rules page. A dialog will open showing all rule details including name, URL, priority, status, schedule, and matching criteria.',
  },
  {
    question: 'How do I edit an existing rule?',
    answer: 'Click the edit button on a rule card. This will open the rule creation form with all existing data pre-filled. After making changes, submit the updated rule — it will go through the approval process again.',
  },
  {
    question: 'How do I test a rule before it goes live?',
    answer: 'Click the test button on a rule card. The Test Rule dialog will open where you can enter latitude, longitude, and other parameters to simulate a QR scan and verify which destination the rule redirects to.',
  },
  {
    question: 'What are the different rule statuses?',
    answer: 'Submitted — Waiting for admin approval. Scheduled — Approved but start date hasn\'t arrived yet. Active — Live and redirecting QR scans. Inactive — Expired or rejected by admin.',
  },
  {
    question: 'When does a rule go live?',
    answer: 'A rule goes live automatically once it is approved by an admin and the start date has arrived.',
  },
  {
    question: 'What happens if two rules match the same QR scan?',
    answer: 'The rule with the highest priority wins. Priority levels from lowest to highest: Low, Medium, High, Critical (Critical is the highest).',
  },
  {
    question: 'What does Critical priority mean?',
    answer: 'Critical is the highest priority. If multiple rules match the same scan, the rule with Critical priority will override the others.',
  },
  {
    question: 'Can I create a rule without an end date?',
    answer: 'Yes, select the "Rule has no expiration date" option. The rule will stay active indefinitely after approval.',
  },
  {
    question: 'Can I schedule a rule to start in the future?',
    answer: 'Yes, set a future start date and time. The rule will have a "Scheduled" status after approval and go live automatically when the start date arrives.',
  },
  {
    question: 'What happens when a rule expires?',
    answer: 'When the end date passes, the rule automatically becomes "Inactive" and QR scans will no longer be redirected by that rule.',
  },
  {
    question: 'Can I create multiple rules at once?',
    answer: 'Yes, use the Bulk Upload feature. Click "Download Format" to get the Excel template, fill in your rules, and upload the file using the Import button.',
  },
  {
    question: 'What does the Download Format button do?',
    answer: 'It downloads an Excel template with two sheets — QR Rule Input (where you fill in rule data) and Country Reference (list of valid country codes). Fill in the template and use Import to upload.',
  },
  {
    question: 'How do I import rules from an Excel file?',
    answer: 'Click the Import button on the Rules page, select your filled-in Excel file (.xlsx format), and the system will parse and create the rules automatically.',
  },
  {
    question: 'How do I export rules?',
    answer: 'Click the Export button on the Rules page. It will download all your current rules as a CSV file. The button is disabled if there are no rules to export.',
  },
  {
    question: 'How does the approval process work?',
    answer: 'When a user submits a rule, it appears on the Approvals page. An admin can then review and approve or reject the rule. If rejected, the admin must provide a reason.',
  },
  {
    question: 'Who can approve or reject rules?',
    answer: 'Only users with the Admin role can approve, reject, or delete rules. Regular users can only create and submit rules.',
  },
  {
    question: 'What matching criteria can I use in a rule?',
    answer: 'You can use Style Codes, Style Color Codes, Country, Brand, Category, Division, GTINs, QR Codes, or Bounding Box. At least one criterion is required for every rule.',
  },
  {
    question: 'What is a bounding box?',
    answer: 'A bounding box allows geographic targeting using latitude and longitude coordinates (North, South, East, West). Only users within the defined area will be redirected.',
  },
  {
    question: 'How do I get bounding box coordinates?',
    answer: 'Use the free tool at https://boundingbox.klokantech.com/ — search for a location, draw a rectangle on the map, and copy the coordinates.',
  },
  {
    question: 'What is the difference between Admin and User roles?',
    answer: 'Admins have full access — they can approve, reject, delete rules, and access the Audit Log. Users can create and submit rules but cannot approve, reject, or delete them.',
  },
  {
    question: 'What is an AD group?',
    answer: 'An AD group (Active Directory group) is like a team list managed by your company. If your name is on the list, you get access. Different lists give different permissions — for example, one list may let you view things, while another lets you approve or delete. The dashboard uses AD groups to decide what each person can do.',
  },
  {
    question: 'What are the AD groups for the dashboard?',
    answer: 'The dashboard uses two AD groups — Admin and User. Admins get full access including approvals, deletions, and the Audit Log. Users can create and submit rules but cannot perform admin actions. Your access level depends on which AD group you belong to. To get access, request membership in the appropriate AD group through ID Locker.',
  },
  {
    question: 'How do I request access to Adobe Analytics?',
    answer: 'Go to the #adobe-analytics-support channel in Slack, click "Request Access," and the accessbot will submit the request for you.',
  },
  {
    question: 'What is Adobe Analytics used for?',
    answer: 'Adobe Analytics helps track how your QR redirect rules are performing — including views, scans, redirects, and user behavior. It shows which rules are most effective.',
  },
  {
    question: 'How do I open Adobe Analytics from the dashboard?',
    answer: 'Click the "Adobe Analytics" button in the header action bar on the Rules page. This opens Adobe Analytics directly in a new tab.',
  },
];

const generateId = (question: string) =>
  question.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const FAQsPage: React.FC<FAQsPageProps> = ({ onNavigate }) => {
  const [highlightedId, setHighlightedId] = useState<string>('');
  const questionRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const scrollToQuestion = (id: string) => {
    setHighlightedId(id);
    const el = questionRefs.current[id];
    const container = scrollContainerRef.current;
    if (el && container) {
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      container.scrollTo({
        top: container.scrollTop + (elTop - containerTop) - 20,
        behavior: 'smooth',
      });
    }
    setTimeout(() => setHighlightedId(''), 2000);
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', background: '#fff', overflow: 'hidden' }}>
      <Box ref={scrollContainerRef} sx={{ flex: 1, overflow: 'auto', px: { xs: 3, md: 5 }, py: 4, borderRight: 'none', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 4 }}>
          Frequently Asked Questions
        </Typography>

        {allFaqs.map((faq) => {
          const id = generateId(faq.question);
          const isHighlighted = highlightedId === id;
          return (
            <Box
              key={id}
              id={id}
              ref={(el: HTMLElement | null) => { questionRefs.current[id] = el; }}
              sx={{
                mb: 0.5,
                p: 1.5,
                mx: -1.5,
                borderRadius: 1,
                bgcolor: isHighlighted ? '#e3f2fd' : 'transparent',
                transition: 'background-color 0.3s ease',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                  color: isHighlighted ? '#1565C0' : '#1a1a1a',
                  mb: 1,
                  lineHeight: 1.4,
                  transition: 'color 0.3s ease',
                }}
              >
                {faq.question}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  color: '#1a1a1a',
                  lineHeight: 1.8,
                }}
              >
                {faq.answer}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          borderLeft: 'none',
          overflow: 'hidden',
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: 'calc(100vh - 64px)',
          pt: 3,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FormatListBulletedIcon sx={{ fontSize: 20, color: '#1565C0' }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              color: '#1a1a1a',
            }}
          >
            On this page
          </Typography>
        </Box>

        <Box sx={{ ml: '9.5px', pt: 1, borderLeft: '1px solid #e0e0e0', flex: 1, overflow: 'auto', pb: 3, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          {allFaqs.map((faq) => {
            const id = generateId(faq.question);
            return (
              <Typography
                key={id}
                onClick={() => scrollToQuestion(id)}
                sx={{
                  fontSize: '14px',
                  color: '#1a1a1a',
                  fontWeight: 400,
                  cursor: 'pointer',
                  py: 0.6,
                  lineHeight: 1.5,
                  pl: 1.5,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    color: '#1565C0',
                  },
                }}
              >
                {faq.question}
              </Typography>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FAQsPage;
